import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Reveal from './Reveal';
import { useApp } from '../store';
import { can } from '../permissions';
import {
  rollupBudget,
  lineBaseAmount,
  phaseBreakdown,
  governanceRollup,
  readRetentionRate,
  controlExceptionIds,
  pendingCorrection,
  INVOICE_STATUS_WORD,
  INVOICE_STATUS_TONE,
  type InvoiceLine,
  type PoolRollup,
} from '../projects';
import { COST_BASIS_LABELS } from '../portfolio';
import type { CostBasis } from '../types';
import { livePhases } from '../phases';
import { MONTHS } from '../when';
import DateField from './DateField';
import { useInvalidDates } from './useInvalidDates';
import { fmtMoney, fmtIn, asAmount, zeroMoney, currencyByCode, CURRENCIES } from '../money';
import { displayName } from '../people';
import BudgetGraph from './BudgetGraph';
import MoneyInput from './MoneyInput';
import Icon, { type IconName } from './Icon';
import { CorrectionDialog } from './CorrectionDialog';
import ScopeAdjustDialog from './ScopeAdjustDialog';
import { useSampleInert } from './SampleStrip';
import { asDate } from '../utils';
import { isProjectMode } from '../lifecycle';
import {
  milestoneBilling,
  milestonesVsContract,
  type MilestoneKind,
} from '../milestones';

/**
 * A LEDGER ROW'S ⋯ MENU — its actions, without opening the line. One for now:
 * Reclassify (change the capex/opex pool or phase). Closes on a click away or Escape (the app-wide menu
 * standard). Clicks are stopped from bubbling so the row beneath does not open.
 */
function RowMenu({ onCorrect }: { onCorrect: () => void }) {
  const inert = useSampleInert();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // The menu is rendered in a portal at <body>, so the ledger list's
  // overflow:hidden (which clips the rows' rounded corners) can't swallow it —
  // the bug where the menu opened "inside the card" and could not be seen.
  // Positioned by hand off the button: below-right when it fits, flipped above
  // when it would run off the bottom, always clamped inside the window.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    function place() {
      const a = btn.current?.getBoundingClientRect();
      const el = pop.current;
      if (!a || !el) return;
      const gap = 4, pad = 8;
      const w = el.offsetWidth, h = el.offsetHeight;
      let left = Math.min(Math.max(pad, a.right - w), window.innerWidth - w - pad);
      const below = a.bottom + gap, above = a.top - gap - h;
      let top = below;
      if (below + h > window.innerHeight - pad && (above >= pad || a.top > window.innerHeight - a.bottom)) top = above;
      top = Math.min(Math.max(pad, top), window.innerHeight - h - pad);
      setPos({ top, left });
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btn.current?.contains(t) || pop.current?.contains(t)) return;
      setOpen(false);
    };
    // Capture-phase Escape that stops here, so one keypress closes only the menu
    // and never also dismisses the budget modal behind it (the app-wide nested-
    // overlay rule, as in CalendarPopover).
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', away, true);
    document.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('mousedown', away, true); document.removeEventListener('keydown', esc, true); };
  }, [open]);

  return (
    <span className="lr-menu" onClick={(e) => e.stopPropagation()}>
      <button ref={btn} className="lr-menu-btn" aria-haspopup="menu" aria-expanded={open} aria-label="More actions" title="More actions"
        onClick={() => setOpen((o) => !o)}>
        <Icon name="ellipsis" size={16} />
      </button>
      {open && createPortal(
        <div ref={pop} className="lr-menu-pop" role="menu"
          style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
          onClick={(e) => e.stopPropagation()}>
          <button role="menuitem" {...inert('', () => { setOpen(false); onCorrect(); })}>
            Reclassify
          </button>
        </div>,
        document.body,
      )}
    </span>
  );
}

/*
 * THE BUDGET SURFACE — budget management + the invoice/commitment ledger, in one
 * place (the budget modal, 0116). The approved budget is the anchor: the total
 * band reads Budget − Committed − Actual = Remaining, and the capex/opex pools
 * are the breakdown beneath it, reconciling to the same total (with any baseline
 * not yet split shown as Unclassified). Committed (raised, unpaid) and Actual
 * (paid) are kept apart, never merged. A line's status MOVES committed → paid.
 * The forecast and the budget verdict are unchanged — this informs them, never
 * overwrites them.
 */

/** 'YYYY-MM-DD' → '15 Sept'. A blank or malformed date reads as an em dash. */
function fmtDay(d?: string): string {
  if (!d) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return d;
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${Number(m[3])} ${mon}` : d;
}

/** 'YYYY-MM-DD' → '18 Jun 2026' — the schedule of values carries the year. */
function fmtDayYear(d?: string | null): string {
  if (!d) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return d;
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${Number(m[3])} ${mon} ${m[1]}` : d;
}

/** The kind glyph on the schedule-of-values row — the same map the Plan register
 *  uses (contract = link, delivery = package, …). */
const SOV_KIND_ICON: Record<MilestoneKind, IconName> = {
  contract: 'link',
  delivery: 'package',
  golive: 'flag',
  signoff: 'task',
  regulatory: 'shield',
  other: 'pin',
};

/** The billing state as the one RAG word on a schedule-of-values row. */
const SOV_BADGE: Record<'paid' | 'claimed' | 'part-claimed' | 'scheduled', { cls: string; word: string }> = {
  paid: { cls: 'g', word: 'Paid' },
  claimed: { cls: 'a', word: 'Claimed' },
  'part-claimed': { cls: 'a', word: 'Part-claimed' },
  scheduled: { cls: 'n', word: 'Scheduled' },
};

function PoolCard({ name, kind, r }: { name: string; kind: string; r: PoolRollup }) {
  const planned = r.planned;
  const rem = r.remaining;
  const remClass = rem == null ? '' : rem < 0 ? 'over' : planned && rem < planned * 0.1 ? 'tight' : '';
  const aW = planned && planned > 0 ? Math.min(r.actual / planned, 1) * 100 : 0;
  const cW = planned && planned > 0 ? Math.min(r.committed / planned, Math.max(0, 1 - r.actual / planned)) * 100 : 0;
  return (
    <div className="bud-pool">
      <div className="bud-pool-top">
        <span className="bud-pool-name">{name}</span>
        <span className="bud-pool-kind">{kind}</span>
        <span className={`bud-pool-rem ${remClass}`}>
          <span className="rl">Remaining</span>
          <span className="rv tnum">{rem == null ? '—' : fmtMoney(rem)}</span>
        </span>
      </div>
      {planned != null && planned > 0 && (
        <div className="bud-pool-bar" title="Actual and committed against the plan">
          <span className="seg-actual" style={{ width: `${aW}%` }} />
          <span className="seg-committed" style={{ width: `${cW}%` }} />
        </div>
      )}
      <div className="bud-pool-grid">
        <span className="bud-cell">
          <span className="cl">Planned</span>
          <span className="cv dim tnum">{planned != null ? fmtMoney(planned) : '—'}</span>
        </span>
        <span className="bud-cell">
          <span className="cl">Committed</span>
          <span className="cv tnum">{fmtMoney(r.committed) || zeroMoney()}</span>
        </span>
        <span className="bud-cell">
          <span className="cl">Actual</span>
          <span className="cv tnum">{fmtMoney(r.actual) || zeroMoney()}</span>
        </span>
      </div>
    </div>
  );
}

export default function BudgetLedger({
  startEditing = false,
  openLineId,
  onOpenLine,
  hideInlineEdit = false,
  editSignal,
}: {
  startEditing?: boolean;
  openLineId?: string | null;
  onOpenLine?: (id: string) => void;
  /** Hide the band's own "Edit budget" button — the Budget & payments SCREEN
   *  drives editing from its header instead, so the control is not duplicated. */
  hideInlineEdit?: boolean;
  /** A counter the screen bumps to open the edit form (its header "Edit budget").
   *  Changing it (not its first value) opens edit; leave undefined to not drive it. */
  editSignal?: number;
}) {
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const project = projects.find((p) => p.id === projectId);
  const role = useApp((s) => s.shownRole);
  const mayPlan = can(role, 'editSettings');
  const inert = useSampleInert();
  const updateProject = useApp((s) => s.updateProject);
  const profiles = useApp((s) => s.profiles);
  const addBudgetLine = useApp((s) => s.addBudgetLine);
  // The base (reporting) currency and the current workspace rates. Every total on
  // this surface is drawn in the base; foreign lines convert through these.
  const base = useApp((s) => s.currency);
  const fxRates = useApp((s) => s.fxRates);
  // The board's phases, for assigning a line and for the by-phase breakdown. Live
  // (non-retired) ones lead the select; the full list still names a retired phase
  // a line was tied to.
  const phases = useApp((s) => s.phases);
  const milestones = useApp((s) => s.milestones);
  const milestoneName = (id?: string): string | undefined => (id ? milestones.find((m) => m.id === id)?.name : undefined);
  // The billable milestones a payment may claim against — offered right in the
  // Add-a-payment form, so the first save links the line (WF-F1); re-linking a
  // settled line stays a governed correction in the drawer.
  const billableMs = milestones.filter((m) => m.billable && m.billValue != null);
  const livePh = useMemo(() => livePhases(phases), [phases]);
  const phaseName = (id?: string): string | undefined => (id ? phases.find((p) => p.id === id)?.name : undefined);

  const rollup = useMemo(() => rollupBudget(project ?? {}, { base, rates: fxRates }), [project, base, fxRates]);
  // The governance headlines (approved-not-paid · awaiting-acceptance · retention
  // held · control exceptions) and the set of lines carrying a control exception.
  const gov = useMemo(() => governanceRollup(project ?? {}, { base, rates: fxRates }), [project, base, fxRates]);
  const excIds = useMemo(() => controlExceptionIds(project?.invoices ?? []), [project]);
  // Spend by phase — same base-currency rule as the rollup, ordered by the board's
  // phase order with any retired-but-used phases and Unassigned appended.
  const phaseRows = useMemo(() => {
    const bd = phaseBreakdown(project ?? {}, { base, rates: fxRates });
    const rows: { key: string; name: string; committed: number; actual: number }[] = [];
    const seen = new Set<string>();
    for (const p of livePh) {
      if (bd[p.id]) { rows.push({ key: p.id, name: p.name, ...bd[p.id] }); seen.add(p.id); }
    }
    // A line tied to a phase that is retired or no longer on the board.
    for (const key of Object.keys(bd)) {
      if (key === '' || seen.has(key)) continue;
      rows.push({ key, name: phaseName(key) ?? 'Other', committed: bd[key].committed, actual: bd[key].actual });
    }
    if (bd['']) rows.push({ key: '', name: 'Unassigned', ...bd[''] });
    return rows;
  }, [project, base, fxRates, livePh, phases]);
  const isForeign = (code?: string) => !!code && code !== base;
  const lines = useMemo(() => {
    const ls = [...(project?.invoices ?? [])];
    ls.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
    return ls;
  }, [project]);

  // THE SCHEDULE OF VALUES — every billable milestone and what the ledger has
  // claimed/paid against it, reconciled to the contract (milestone payments; mock
  // 4). Reads the SAME ledger as the Plan register, so the two never disagree. The
  // milestone value is a slice of the contract — never added into spend. Project
  // mode only, and only when there is at least one billable milestone.
  const sov = useMemo(() => {
    if (!project || !isProjectMode(project)) return null;
    const billable = milestones.filter((m) => m.billable && m.billValue != null);
    if (billable.length === 0) return null;
    const rows = billable
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name))
      .map((m) => ({ m, b: milestoneBilling(m.id, m.billValue as number, lines) }));
    const totals = rows.reduce(
      (t, { b }) => ({
        scheduled: t.scheduled + b.scheduled,
        claimed: t.claimed + b.claimed,
        paid: t.paid + b.paid,
        remaining: t.remaining + Math.max(0, b.remaining),
      }),
      { scheduled: 0, claimed: 0, paid: 0, remaining: 0 },
    );
    const recon = milestonesVsContract(billable, project.contractValue);
    return { rows, totals, recon };
  }, [project, milestones, lines]);

  // Budget management — the approved baseline, its capex/opex split and the
  // forecast, edited in place. Opened straight into edit when the Plan sent us
  // here to set the budget or add a forecast.
  const numStr = (n?: number | null) => (n != null ? String(n) : '');
  const [editing, setEditing] = useState(startEditing && mayPlan);
  const [bAmt, setBAmt] = useState<number | undefined>(project?.budget);
  // The agreed contract value the baseline is based on (#435). Entering it
  // auto-populates the baseline (Q1), but the baseline stays editable — once a
  // person edits the baseline directly, `baselineTouched` stops the auto-sync so
  // their figure is not overwritten the next time the contract value changes.
  const [bContract, setBContract] = useState<number | undefined>(project?.contractValue);
  const [baselineTouched, setBaselineTouched] = useState(false);
  // Changing an already-set contract value is a recorded decision, not a field edit
  // (#435, R22) — it opens the focused ScopeAdjustDialog, which holds its own new
  // value / reason / document-reference state.
  const [adjOpen, setAdjOpen] = useState(false);
  const [bFund, setBFund] = useState(project?.fundingSource ?? '');
  // How the vendor(s) are paid — tunes how the cost tile reads CPI vs the forecast
  // verdict (0219). '' = unset = the balanced read.
  const [bCostBasis, setBCostBasis] = useState<CostBasis | ''>(project?.costBasis ?? '');
  const [bCapex, setBCapex] = useState<number | undefined>(project?.capex);
  const [bCapexNote, setBCapexNote] = useState(project?.capexNote ?? '');
  const [bOpex, setBOpex] = useState<number | undefined>(project?.opex);
  const [bOpexNote, setBOpexNote] = useState(project?.opexNote ?? '');
  const [bForecast, setBForecast] = useState<number | undefined>(project?.forecast);
  const [bRetention, setBRetention] = useState(numStr(project?.retentionRate));

  const openEdit = () => {
    setBAmt(project?.budget);
    setBContract(project?.contractValue);
    setBaselineTouched(false);
    setBFund(project?.fundingSource ?? '');
    setBCostBasis(project?.costBasis ?? '');
    setBCapex(project?.capex);
    setBCapexNote(project?.capexNote ?? '');
    setBOpex(project?.opex);
    setBOpexNote(project?.opexNote ?? '');
    setBForecast(project?.forecast);
    setBRetention(numStr(project?.retentionRate));
    setEditing(true);
  };

  // The screen's header "Edit budget" opens the form from outside — it bumps
  // editSignal, and we open on the change (never on the first value, so mounting
  // on the screen does not force the form open).
  const prevSignal = useRef(editSignal);
  useEffect(() => {
    if (editSignal !== undefined && editSignal !== prevSignal.current) {
      prevSignal.current = editSignal;
      if (mayPlan) openEdit();
    }
    // openEdit reads the latest project through closure each call; editSignal is
    // the only trigger we watch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSignal]);
  const saveBudget = () => {
    const amt = bAmt;
    // A baseline OR a contract value is enough to save — the two are the anchor;
    // an empty form stays a no-op.
    if (amt != null || bContract != null) {
      void updateProject(projectId, {
        budget: amt,
        // Contract value is SET here only the first time. Once a project has one,
        // it changes only through the recorded scope-adjustment flow, so a plain
        // budget save never rewrites it (and never logs a second decision line).
        ...(project?.contractValue == null ? { contractValue: bContract } : {}),
        fundingSource: bFund.trim() || undefined,
        costBasis: bCostBasis || undefined,
        capex: bCapex,
        capexNote: bCapexNote.trim() || undefined,
        opex: bOpex,
        opexNote: bOpexNote.trim() || undefined,
        forecast: bForecast,
        // null when the field is cleared (retention lifted), 0 for an explicit
        // "no retention", the number otherwise — never undefined, so a cleared or
        // zeroed rate is actually written rather than dropped and re-defaulted.
        retentionRate: readRetentionRate(bRetention),
      });
    }
    setEditing(false);
  };
  const reconcile = (() => {
    const base = bAmt;
    const cx = bCapex;
    const ox = bOpex;
    if (base == null || (cx == null && ox == null)) return null;
    const sum = (cx ?? 0) + (ox ?? 0);
    return { sum, diff: sum - base };
  })();

  // The add-a-line form.
  const [adding, setAdding] = useState(false);
  // The line whose correction dialog is open (from a row's ⋯ menu).
  const [correctingLine, setCorrectingLine] = useState<InvoiceLine | null>(null);
  // Hold the add/edit save while its date box holds an entry it could not read
  // (see useInvalidDates). Add and edit are never open at once, so one tracker
  // serves both — its field's key clears on unmount when the panel closes.
  const badDates = useInvalidDates();
  const [amount, setAmount] = useState('');
  const [vendor, setVendor] = useState('');
  const [pool, setPool] = useState<'capex' | 'opex'>('capex');
  const [status, setStatus] = useState<'committed' | 'paid'>('committed');
  const [date, setDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [curCode, setCurCode] = useState(base);
  const [rate, setRate] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [milestoneId, setMilestoneId] = useState('');
  const [problem, setProblem] = useState('');
  // The supplier invoice, attached as the payment is added (optional on a committed
  // line, where the PO is raised before the invoice arrives). Held as a File until
  // the line exists, then uploaded in the same action.
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const addFileRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);

  // The currency + rate fields to SET on a line, or an error when a foreign line
  // has no rate to convert with. A base-currency line sets nothing (clearing on an
  // edit is handled separately, with null, so the stored jsonb key is dropped). A
  // typed rate wins; otherwise the current workspace rate is captured onto the line.
  const currencyPart = (code: string, rateStr: string): { set: Partial<InvoiceLine> } | { error: string } => {
    if (!isForeign(code)) return { set: {} };
    const resolved = asAmount(rateStr) ?? fxRates[code];
    if (resolved == null) return { error: `Enter the exchange rate for ${currencyByCode(code).code}.` };
    return { set: { currency: code, fxRate: resolved } };
  };
  // Picking a currency in a form seeds its rate box from the workspace rate.
  const pickCurrency = (code: string, setC: (v: string) => void, setR: (v: string) => void) => {
    setC(code);
    setR(code !== base && fxRates[code] != null ? String(fxRates[code]) : '');
  };
  const liveEquiv = (amountStr: string, code: string, rateStr: string): string => {
    const amt = asAmount(amountStr);
    if (amt == null || !isForeign(code)) return '';
    const r = asAmount(rateStr) ?? fxRates[code];
    if (r == null) return '';
    return `= ${fmtMoney(amt * r)} in ${currencyByCode(base).code}`;
  };

  if (!project) return null;

  const hasBudget = project.budget != null;
  const hasLines = lines.length > 0;

  // The baseline follows the contract value until a person edits it directly
  // (baselineTouched). Before any contract is entered the baseline waits on it;
  // once the contract is typed the baseline mirrors it; editing the baseline breaks
  // the tether, so a deliberately-different baseline (a held contingency) is never
  // overwritten by the next contract change.
  const effContract = bContract ?? project.contractValue;
  const baselineWaiting = effContract == null && !baselineTouched && bAmt == null;
  // The live baseline↔contract relationship, shown as one helper attached under the
  // Baseline field (R22; budget-baseline-placement, approved 9 Oct). Keyed off the
  // live equality — not `baselineTouched` — so matching the contract by hand also
  // reads as "following", and any held difference always names its own delta.
  const baselineHasContract = effContract != null && bAmt != null;
  const baselineContingency = baselineHasContract ? bAmt - effContract : 0;
  const spentPlusCommitted = rollup.committedTotal + rollup.actualTotal;
  const overBudget = rollup.budget != null && spentPlusCommitted > rollup.budget;
  // Compare what the reader SEES: money shows in whole base units, and an FX-
  // converted ledger carries cents, so a forecast reconciled to the ledger can
  // land a fraction over it. Rounding both sides keeps "TT$0 past forecast" from
  // reading as out of date when the two are level on screen.
  const forecastStale =
    project.forecast != null && Math.round(spentPlusCommitted) > Math.round(project.forecast);

  const resetAdd = () => {
    setAmount(''); setVendor(''); setPool('capex'); setStatus('committed');
    setDate(''); setDueDate(''); setDescription(''); setInvoiceRef(''); setPoNumber('');
    setCurCode(base); setRate(''); setPhaseId(''); setMilestoneId(''); setProblem('');
    setInvoiceFile(null); setSaving(false);
  };
  const submit = async () => {
    if (badDates.ref.current) return;   // the date box holds something unreadable
    if (saving) return;
    const amt = asAmount(amount);
    if (amt == null) { setProblem('Enter an amount.'); return; }
    if (!vendor.trim()) { setProblem('Name the vendor.'); return; }
    const cur = currencyPart(curCode, rate);
    if ('error' in cur) { setProblem(cur.error); return; }
    const line: Omit<InvoiceLine, 'id'> = {
      amount: amt, pool, status, vendor: vendor.trim(),
      ...(date.trim() ? { date: date.trim() } : {}),
      ...(dueDate.trim() ? { dueDate: dueDate.trim() } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(invoiceRef.trim() ? { invoiceRef: invoiceRef.trim() } : {}),
      ...(poNumber.trim() ? { poNumber: poNumber.trim() } : {}),
      ...(phaseId ? { phase: phaseId } : {}),
      ...(milestoneId ? { milestone: milestoneId } : {}),
      // A committed line captures the PO figure, so a later-edited invoice amount
      // can be checked against what was raised (the drawer's PO-vs-invoice variance).
      ...(status === 'committed' ? { poAmount: amt } : {}),
      ...cur.set,
    };
    setSaving(true); setProblem('');
    const err = await addBudgetLine(project.id, line, invoiceFile ?? undefined);
    if (err) { setProblem(err); setSaving(false); return; }
    resetAdd();
    setAdding(false);
  };
  const onPickAddFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setInvoiceFile(f);
    e.target.value = '';
  };

  // ── Budget management (the figures) ──
  const remClass = rollup.remaining == null ? '' : rollup.remaining < 0 ? 'over' : '';
  const budgetW = rollup.budget && rollup.budget > 0 ? rollup.budget : 0;
  const bandActualW = budgetW ? Math.min(rollup.actualTotal / budgetW, 1) * 100 : 0;
  const bandCommW = budgetW ? Math.min(rollup.committedTotal / budgetW, Math.max(0, 1 - rollup.actualTotal / budgetW)) * 100 : 0;

  // ── The "Forecast vs the ledger" band (#433) — the PM's typed forecast read
  //    against the ledger's own committed + paid, every modal open. It never
  //    rewrites the typed figure; when the ledger passes it, it flags and offers a
  //    one-click reconcile. The bar is scaled to the largest of budget / forecast /
  //    ledger so nothing overflows; the two ticks stay at the forecast and budget. */
  const fbBudget = rollup.budget ?? 0;
  const fbLedger = spentPlusCommitted; // committed + paid, from the ledger
  const fbVariance = project.forecast != null ? project.forecast - fbLedger : 0; // + = forecast still above the ledger
  const fbScale = Math.max(fbBudget, project.forecast ?? 0, fbLedger, 1);
  const fbPaidW = Math.min(rollup.actualTotal / fbScale, 1) * 100;
  const fbCommW = Math.min(rollup.committedTotal / fbScale, Math.max(0, 1 - rollup.actualTotal / fbScale)) * 100;
  const fbForecastPos = Math.min((project.forecast ?? 0) / fbScale, 1) * 100;
  const fbBudgetPos = Math.min(fbBudget / fbScale, 1) * 100;
  // In the common forecast≈budget case the two ticks land on the same spot and the
  // grey Budget tick would hide the teal Forecast one — so draw a single combined
  // tick then. And clamp each tick's label so a mark near the ends never spills its
  // centre-anchored label past the card edge (right-anchor near 100%, left near 0%).
  const ticksCoincide = Math.abs(fbForecastPos - fbBudgetPos) <= 1.5;
  const lblShift = (pos: number) => (pos >= 82 ? 'translateX(-100%)' : pos <= 12 ? 'translateX(0)' : 'translateX(-50%)');

  return (
    <div className="bud">
      {/* THE TOTAL BAND — the ledger tied to the approved budget. */}
      {hasBudget && !editing && (
        <div className="bud-total">
          <div className="bt-top">
            <span className="bt-name">Budget</span>
            <span className="bt-sub">
              approved baseline{project.fundingSource ? ` · ${project.fundingSource}` : ''}
            </span>
            <span className={`bt-rem ${remClass}`}>
              <span className="rl">Remaining of budget</span>
              <span className="rv tnum">{rollup.remaining != null ? fmtMoney(rollup.remaining) : '—'}</span>
            </span>
            {mayPlan && !hideInlineEdit && (
              <button {...inert('btn btn-small bt-editbtn', openEdit, 'Change the approved budget, split or forecast')}>
                <Icon name="pencil" size={13} /> Edit budget
              </button>
            )}
          </div>
          {budgetW > 0 && (
            <div className="bar" title="Actual and committed against the approved budget">
              <span className="seg-actual" style={{ width: `${bandActualW}%` }} />
              <span className="seg-committed" style={{ width: `${bandCommW}%` }} />
            </div>
          )}
          <div className="bt-grid">
            {project.contractValue != null && (
              <span className="bt-cell"><span className="cl">Contract value</span><span className="cv dim tnum">{fmtMoney(project.contractValue)}</span></span>
            )}
            <span className="bt-cell"><span className="cl">Budget (baseline)</span><span className="cv dim tnum">{fmtMoney(rollup.budget)}</span></span>
            <span className="bt-cell"><span className="cl">Committed</span><span className="cv tnum">{fmtMoney(rollup.committedTotal) || zeroMoney()}</span></span>
            <span className="bt-cell"><span className="cl">Actual (paid)</span><span className="cv tnum">{fmtMoney(rollup.actualTotal) || zeroMoney()}</span></span>
            <span className="bt-cell"><span className="cl">Remaining</span><span className={`cv tnum ${remClass}`}>{rollup.remaining != null ? fmtMoney(rollup.remaining) : '—'}</span></span>
          </div>
        </div>
      )}

      {/* How the contract value got here — the recorded scope changes, on the READ
          view so a reader sees the trail without entering the edit form (WF-F4). */}
      {!editing && (project.contractAdjustments?.length ?? 0) > 0 && (
        <div className="bud-adj-read">
         <Reveal summary={`Scope Changes (${project.contractAdjustments!.length})`}>
          <ul className="bud-adj-list">
            {project.contractAdjustments!.slice().reverse().map((a) => (
              <li key={a.id} className="bud-adj-item">
                <span className="bud-adj-amt tnum">{fmtMoney(a.from)} &rarr; {fmtMoney(a.to)}</span>
                <span className="bud-adj-mid">
                  <span className="bud-adj-reason">{a.reason}</span>
                  <span className="bud-adj-by">Approved by {displayName(a.by, profiles)} &middot; {asDate(a.at)}</span>
                </span>
                {a.documentRef ? (
                  <span className="bud-adj-doc"><Icon name="document" size={13} /> {a.documentRef}</span>
                ) : null}
              </li>
            ))}
          </ul>
         </Reveal>
        </div>
      )}

      {/* THE SCHEDULE OF VALUES — one row per billable milestone and what is claimed
          against it, reconciled to the contract (milestone payments; mock 4). The
          same four figures as the Plan register's inline read, from the same ledger. */}
      {sov && !editing && (
        <div className="bud-sov">
          <div className="bud-breaklabel bud-sov-h">
            <Icon name="coin" size={16} /> Schedule of values — the billable milestones and what&rsquo;s claimed against each
          </div>
          <div className="bud-table-wrap">
            <table className="bud-table bud-sov-table">
              <thead>
                <tr>
                  <th>Milestone</th>
                  <th>Target</th>
                  <th className="num">Scheduled</th>
                  <th className="num">Claimed to date</th>
                  <th className="num">Paid</th>
                  <th className="num">Remaining</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {sov.rows.map(({ m, b }) => {
                  const badge = SOV_BADGE[b.state];
                  return (
                    <tr key={m.id}>
                      <td>
                        <span className="bud-sov-ms">
                          <span className={`bud-sov-glyph k-${m.kind}`} aria-hidden="true">
                            <Icon name={SOV_KIND_ICON[m.kind]} size={16} />
                          </span>
                          {m.name}
                        </span>
                      </td>
                      <td className="tnum">{fmtDayYear(m.targetDay)}</td>
                      <td className="num tnum">{fmtMoney(b.scheduled)}</td>
                      <td className="num tnum">{fmtMoney(b.claimed)}</td>
                      <td className="num tnum bud-sov-paid">{fmtMoney(b.paid)}</td>
                      <td className="num tnum">{fmtMoney(Math.max(0, b.remaining))}</td>
                      <td>
                        <span className={`rag ${badge.cls}`}><span className="dot" />{badge.word}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>All billable milestones</td>
                  <td className="num tnum">{fmtMoney(sov.totals.scheduled)}</td>
                  <td className="num tnum">{fmtMoney(sov.totals.claimed)}</td>
                  <td className="num tnum">{fmtMoney(sov.totals.paid)}</td>
                  <td className="num tnum">{fmtMoney(sov.totals.remaining)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          {sov.recon.contractValue != null && (
            <div className="bud-sov-recon">
              <span className="bud-sov-recon-mark" aria-hidden="true"><Icon name="coin" size={18} /></span>
              <div className="bud-sov-recon-main">
                <p className="bud-sov-recon-line">
                  Milestones total <b className="tnum">{fmtMoney(sov.recon.billableTotal)}</b> of the{' '}
                  <b className="tnum">{fmtMoney(sov.recon.contractValue)}</b> contract
                  {sov.recon.unallocated != null && sov.recon.unallocated !== 0 && (
                    <>
                      {' '}&mdash;{' '}
                      <b className="tnum bud-sov-recon-gap">
                        {fmtMoney(Math.abs(sov.recon.unallocated))} {sov.recon.unallocated > 0 ? 'unallocated' : 'over the contract'}
                      </b>
                    </>
                  )}
                  .
                </p>
                <div className="bud-sov-recon-bar" aria-hidden="true">
                  <span
                    className="bud-sov-recon-fill"
                    style={{ width: `${Math.min(100, (sov.recon.billableTotal / sov.recon.contractValue) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="bud-sov-recon-guide">A guide, not a limit — the model drops any hard &ldquo;sum to contract.&rdquo;</span>
            </div>
          )}
        </div>
      )}

      {/* FORECAST VS THE LEDGER (#433) — the PM's typed estimate-at-completion read
          against the ledger's own committed + paid. Never rewrites the typed figure;
          flags when the ledger passes it and offers a one-click reconcile. */}
      {hasBudget && !editing && project.forecast != null && hasLines && (
        <div className="fvl">
          <div className="fvl-head">
            <span className="fvl-h">Forecast vs the ledger</span>
            {forecastStale ? (
              <span className="fvl-chip stale"><Icon name="warning" size={13} /> Out of date</span>
            ) : (
              <span className="fvl-chip ok"><Icon name="check" size={13} /> In step with the ledger</span>
            )}
          </div>

          <div className="fvl-barwrap">
            <span className="fvl-ticklbl top" style={{ left: `${fbForecastPos}%`, transform: lblShift(fbForecastPos) }}>Forecast</span>
            <div className="fvl-bar">
              <span className="seg-actual" style={{ width: `${fbPaidW}%` }} />
              <span className="seg-committed" style={{ width: `${fbCommW}%` }} />
              {/* Budget tick under the forecast tick; skipped when they coincide so
                  the teal Forecast mark is never hidden by the grey Budget one. */}
              {!ticksCoincide && <span className="fvl-tick budget" style={{ left: `${fbBudgetPos}%` }} />}
              <span className="fvl-tick forecast" style={{ left: `${fbForecastPos}%` }} />
            </div>
            <span className="fvl-ticklbl bot" style={{ left: `${fbBudgetPos}%`, transform: lblShift(fbBudgetPos) }}>Budget</span>
          </div>

          <div className="fvl-read">
            <div className="fvl-cell">
              <span className="fvl-cl">Forecast at completion</span>
              <span className="fvl-cv tnum">{fmtMoney(project.forecast)}</span>
              <span className="fvl-cs">you typed this</span>
            </div>
            <div className="fvl-cell">
              <span className="fvl-cl">Committed + paid</span>
              <span className="fvl-cv tnum">{fmtMoney(fbLedger)}</span>
              <span className="fvl-cs">from the ledger</span>
            </div>
            <div className="fvl-cell fvl-varcell">
              <span className="fvl-cl">Variance</span>
              {forecastStale ? (
                <span className="fvl-delta stale">▲ {fmtMoney(Math.abs(fbVariance))} past forecast</span>
              ) : (
                <span className="fvl-delta ok">▼ {fmtMoney(Math.abs(fbVariance))} under forecast</span>
              )}
            </div>
          </div>

          <p className="fvl-line">
            {forecastStale ? (
              <>
                Committed + paid <b>{fmtMoney(fbLedger)}</b> has passed the <b>{fmtMoney(project.forecast)}</b>{' '}
                forecast by <b>{fmtMoney(Math.abs(fbVariance))}</b> — the forecast is out of date
                {fbLedger <= fbBudget ? (
                  <>, though still <b>{fmtMoney(fbBudget - fbLedger)}</b> inside the {fmtMoney(fbBudget)} budget.</>
                ) : (
                  <>, and now <b>{fmtMoney(fbLedger - fbBudget)}</b> over the {fmtMoney(fbBudget)} budget as well.</>
                )}
                {mayPlan && (
                  <button {...inert('fvl-act', () => { openEdit(); setBForecast(Math.round(fbLedger)); })}>
                    {' '}Update forecast to {fmtMoney(fbLedger)} →
                  </button>
                )}
              </>
            ) : (
              <>
                The <b>{fmtMoney(project.forecast)}</b> forecast still sits above the <b>{fmtMoney(fbLedger)}</b>{' '}
                committed + paid — <b>{fmtMoney(Math.max(0, fbVariance))}</b> of it unspent, and{' '}
                {project.forecast <= fbBudget ? (
                  <><b>{fmtMoney(fbBudget - project.forecast)}</b> under the {fmtMoney(fbBudget)} budget.</>
                ) : (
                  <><b>{fmtMoney(project.forecast - fbBudget)}</b> over the {fmtMoney(fbBudget)} budget.</>
                )}
              </>
            )}
          </p>
        </div>
      )}

      {/* THE GOVERNANCE LINE — the decision-tier headlines, read once beneath the
          budget band (not per row). Retention is a headline only. */}
      {hasBudget && !editing && hasLines && (
        <div className="bpos">
          <span className="bpos-item"><span className="bpl">Approved, not paid</span><span className="bpv tnum">{fmtMoney(gov.approvedNotPaid) || zeroMoney()}</span></span>
          <span className="bpos-div" />
          <span className="bpos-item"><span className="bpl">Awaiting acceptance</span><span className="bpv tnum">{fmtMoney(gov.awaitingAcceptance) || zeroMoney()}</span></span>
          <span className="bpos-div" />
          <span className="bpos-item"><span className="bpl bpl-lock"><Icon name="lock" size={13} /> Retention held</span><span className="bpv tnum">{fmtMoney(gov.retentionHeld) || zeroMoney()}</span></span>
          <span className="bpos-div" />
          {gov.exceptions > 0 ? (
            <span className="bpos-exc"><Icon name="warning" size={13} /><span><b>{gov.exceptions}</b> control {gov.exceptions === 1 ? 'exception' : 'exceptions'}</span></span>
          ) : (
            <span className="bpos-exc none"><Icon name="check" size={13} /><span>No control exceptions</span></span>
          )}
        </div>
      )}

      {/* No budget yet — the one thing to do is set it. */}
      {!hasBudget && !editing && (
        <div className="bud-nobudget">
          <p>No budget set yet. The approved budget is the quote every invoice and commitment tracks against.</p>
          {mayPlan && <button {...inert('btn btn-primary btn-small', openEdit)}>Set the budget</button>}
        </div>
      )}

      {/* Budget management form — THE form pattern (R17): grouped, labelled
          sections, a two-column grid for the short coupled fields, one primary. */}
      {editing && (
        // form-opens: the editor glides in on a drawn rise + fade you actually see
        // (R32 — a reveal is paced to be SEEN), louder than the subtle nav flick.
        // Already in the prefers-reduced-motion snap list, so reduced motion shows it
        // at once. (Vinnie, 1 Nov — the plain view-in read as "not gliding".)
        <div className="bud-editform fd-form form-opens">
          <div className="fd-head">
            <div className="fd-head-main">
              <h2 className="fd-title">Edit budget</h2>
              <p className="fd-sub">
                The contract value is the agreed total the budget is based on — the project&rsquo;s quote.
                The capex/opex split says how it&rsquo;s classified; the forecast lets the report say whether
                you&rsquo;re on track. Leave any blank.
              </p>
            </div>
          </div>

          <section className="fd-sec">
            <h3 className="fd-sec-h">Budget</h3>
            <div className="fd-grid">
              {/* Contract value FIRST — it is the agreed total the baseline is based
                  on, so it leads and populates the baseline below (#435, surface 01). */}
              <label className="stack">
                <span>Contract value</span>
                {project.contractValue == null ? (
                  <>
                    <MoneyInput
                      value={bContract}
                      onChange={(n) => { setBContract(n); if (!baselineTouched) setBAmt(n); }}
                      autoFocus={!startEditing}
                      ariaLabel="Contract value"
                      placeholder="e.g. 636774"
                    />
                    <span className="hint">The agreed total the budget is based on — the project&rsquo;s quote.</span>
                  </>
                ) : (
                  <>
                    <div className="bud-contract-set">
                      <span className="tnum">{fmtMoney(project.contractValue)}</span>
                      {mayPlan && (
                        <button type="button" {...inert('btn btn-small', () => setAdjOpen(true))}>
                          Adjust&hellip;
                        </button>
                      )}
                    </div>
                    <span className="hint">Changing the contract value is a scope change — it needs a reason and is recorded.</span>
                  </>
                )}
              </label>
              <label className="stack">
                <span>Baseline (approved budget)</span>
                <MoneyInput
                  value={bAmt}
                  onChange={(n) => { setBAmt(n); setBaselineTouched(true); }}
                  disabled={baselineWaiting}
                  ariaLabel="Baseline, the approved budget"
                  placeholder={baselineWaiting ? 'Waiting on the contract value' : 'e.g. 636774'}
                />
                {/* One helper, attached under the Baseline field, carrying the live
                    state of the baseline↔contract link (R22; approved 9 Oct). While the
                    baseline mirrors the contract it reads "Following"; the moment it is
                    edited to differ the link breaks (amber), names the held contingency
                    and its exact delta, and offers a one-click way back. */}
                {baselineHasContract && (
                  baselineContingency === 0 ? (
                    <span className="bl-link">
                      <Icon name="link" size={14} />
                      <span><span className="bl-link-strong">Following the contract value.</span> Edit to hold a contingency — the two will then differ.</span>
                    </span>
                  ) : (
                    <span className="bl-link is-diff">
                      <Icon name="unlink" size={14} />
                      <span>
                        <span className="bl-link-strong">Set {baselineContingency > 0 ? 'above' : 'below'} the contract value — a {fmtMoney(Math.abs(baselineContingency))} contingency.</span>{' '}
                        <button type="button" className="bl-link-reset" onClick={() => { setBAmt(effContract); setBaselineTouched(false); }}>Match the contract value</button>
                      </span>
                    </span>
                  )
                )}
              </label>

              <label className="stack">
                <span>Funding source</span>
                <input className="input" placeholder="e.g. FY26 capital programme"
                  value={bFund} onChange={(e) => setBFund(e.target.value)} />
              </label>

              <label className="stack">
                <span>Cost basis</span>
                <select className="input" value={bCostBasis}
                  onChange={(e) => setBCostBasis(e.target.value as CostBasis | '')}
                  aria-label="Cost basis — how the vendor is paid">
                  <option value="">Not set</option>
                  <option value="fixed">{COST_BASIS_LABELS.fixed}</option>
                  <option value="tm">{COST_BASIS_LABELS.tm}</option>
                  <option value="mixed">{COST_BASIS_LABELS.mixed}</option>
                </select>
                <span className="hint">How the vendor is paid — it shapes how the cost read is presented. On a fixed price, spend tracks the payment schedule, so the forecast against the baseline leads over the efficiency index.</span>
              </label>

              {(project.contractAdjustments?.length ?? 0) > 0 && (
                <div className="fd-wide bud-adj-log">
                  <h4 className="bud-adjust-h">Scope Changes</h4>
                  <ul className="bud-adj-list">
                    {project.contractAdjustments!.slice().reverse().map((a) => (
                      <li key={a.id} className="bud-adj-item">
                        <span className="bud-adj-amt tnum">{fmtMoney(a.from)} &rarr; {fmtMoney(a.to)}</span>
                        <span className="bud-adj-mid">
                          <span className="bud-adj-reason">{a.reason}</span>
                          <span className="bud-adj-by">Approved by {displayName(a.by, profiles)} &middot; {asDate(a.at)}</span>
                        </span>
                        {a.documentRef ? (
                          <span className="bud-adj-doc"><Icon name="document" size={13} /> {a.documentRef}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>

          <section className="fd-sec">
            <h3 className="fd-sec-h">Classification — capex / opex split</h3>
            <div className="fd-grid">
              <label className="stack">
                <span>Capex amount</span>
                <MoneyInput value={bCapex} onChange={setBCapex} placeholder="Capital spend" />
              </label>
              <label className="stack">
                <span>Capex note</span>
                <input className="input" placeholder="e.g. hardware &amp; licences" value={bCapexNote} onChange={(e) => setBCapexNote(e.target.value)} />
              </label>
              <label className="stack">
                <span>Opex amount</span>
                <MoneyInput value={bOpex} onChange={setBOpex} placeholder="Operating spend" />
              </label>
              <label className="stack">
                <span>Opex note</span>
                <input className="input" placeholder="e.g. implementation &amp; subscriptions" value={bOpexNote} onChange={(e) => setBOpexNote(e.target.value)} />
              </label>
              {reconcile && (
                <div className={`fd-wide bud-ef-recon ${reconcile.diff === 0 ? 'ok' : 'off'}`}>
                  {reconcile.diff === 0 ? (
                    <><Icon name="check" size={14} /> Capex + opex = {fmtMoney(reconcile.sum)} — matches the baseline.</>
                  ) : (
                    <><Icon name="warning" size={14} /> Capex + opex = {fmtMoney(reconcile.sum)} — {fmtMoney(Math.abs(reconcile.diff))} {reconcile.diff > 0 ? 'over' : 'under'} the baseline. Fine while you&rsquo;re still estimating.</>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="fd-sec">
            <h3 className="fd-sec-h">Forecast &amp; Retention</h3>
            <div className="fd-grid">
              <label className="stack">
                <span>Forecast at completion</span>
                <MoneyInput value={bForecast} onChange={setBForecast} autoFocus={startEditing} placeholder="Estimate at completion" />
              </label>
              <label className="stack">
                <span>Retention rate (%)</span>
                <input className="input" inputMode="numeric" placeholder="e.g. 10"
                  value={bRetention} onChange={(e) => setBRetention(e.target.value)} />
              </label>
            </div>
            <p className="bud-ef-foot">Actual spend isn&rsquo;t typed here — it adds up from the invoices and commitments below.</p>
          </section>

          <div className="fd-actions bud-ef-actions">
            <button className="btn btn-primary btn-small" onClick={saveBudget}>Save budget</button>
            <button className="btn btn-small" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Feedback — informs, never overwrites. */}
      {/* Over budget is the one red escalation — committed + paid past the approved
          baseline. The forecast reconciliation (amber) lives in the band above; a
          project over BOTH forecast and budget shows this red, not a second one. */}
      {hasBudget && !editing && overBudget && (
        <p className="bud-flag">
          <Icon name="warning" size={13} />{' '}
          Committed + paid {fmtMoney(spentPlusCommitted)} is over the {fmtMoney(rollup.budget)} budget.
        </p>
      )}

      {/* When the project holds more than one currency, say totals are in the base
          and how the others got there — right under the band they qualify. */}
      {!editing && rollup.foreignCount != null && (
        <p className="bud-fxnote">
          <i />Totals are in <b>{currencyByCode(base).symbol} {currencyByCode(base).code}</b> (base).{' '}
          {rollup.foreignCount} {rollup.foreignCount === 1 ? 'line' : 'lines'} in another currency{' '}
          {rollup.foreignCount === 1 ? 'was' : 'were'} converted — a paid line at the rate it was paid at, a
          committed line at the current rate — shown on each line below.
        </p>
      )}

      {/* Cost over time — the burn-up, read against the budget and the forecast.
          Lives in the budget popup, never on the always-visible Plan summary. */}
      {!editing && hasLines && <BudgetGraph project={project} rollup={rollup} base={base} rates={fxRates} />}

      {/* Pools — the breakdown beneath the total. */}
      {hasBudget && !editing && (
        <>
          <div className="bud-breaklabel">By classification — reconciles to the budget above</div>
          <div className="bud-pools">
            <PoolCard name="Capex" kind="hardware & licences" r={rollup.capex} />
            <PoolCard name="Opex" kind="implementation & subscriptions" r={rollup.opex} />
            {rollup.unclassified != null && Math.abs(rollup.unclassified) >= 1 && (
              <div className="bud-pool unclassified">
                <div className="bud-pool-top">
                  <span className="bud-pool-name">{rollup.unclassified < 0 ? 'Over-split' : 'Unclassified'}</span>
                  <span className="bud-pool-rem"><span className="rl">Of budget</span><span className="rv tnum">{fmtMoney(Math.abs(rollup.unclassified))}</span></span>
                </div>
                <p className="bud-unclass-note">
                  {rollup.unclassified < 0
                    ? 'Capex + opex is more than the baseline.'
                    : 'Not yet split into capex or opex.'}
                  {mayPlan && <button {...inert('bud-classify', openEdit)}> {rollup.unclassified < 0 ? 'Adjust →' : 'Classify →'}</button>}
                </p>
              </div>
            )}
          </div>
          <div className="bud-legend">
            <span><i className="bud-swatch a" />Actual — paid</span>
            <span><i className="bud-swatch c" />Committed — raised, not yet paid</span>
          </div>
        </>
      )}

      {/* By phase — the same spend cut by phase, beside the capex/opex split. A
          spend breakdown only (no per-phase budget); shown once at least one line
          is assigned to a phase. */}
      {!editing && phaseRows.some((r) => r.key !== '') && (
        <>
          <div className="bud-breaklabel">By phase — where the spend is going</div>
          <div className="bud-table-wrap">
            <table className="bud-table bud-phtable">
              <thead>
                <tr><th>Phase</th><th className="num">Committed</th><th className="num">Actual (paid)</th><th className="num">Total</th></tr>
              </thead>
              <tbody>
                {phaseRows.map((r) => (
                  <tr key={r.key || '_unassigned'}>
                    <td>
                      {r.key ? (
                        <span className="phase-chip"><span className="phase-chip-dot" />{r.name}</span>
                      ) : (
                        <span className="bud-ph-unassigned">Unassigned</span>
                      )}
                    </td>
                    <td className="num tnum">{fmtMoney(r.committed) || zeroMoney()}</td>
                    <td className="num tnum">{fmtMoney(r.actual) || zeroMoney()}</td>
                    <td className="num tnum">{fmtMoney(r.committed + r.actual) || zeroMoney()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>All phases</td>
                  <td className="num tnum">{fmtMoney(rollup.committedTotal) || zeroMoney()}</td>
                  <td className="num tnum">{fmtMoney(rollup.actualTotal) || zeroMoney()}</td>
                  <td className="num tnum">{fmtMoney(rollup.committedTotal + rollup.actualTotal) || zeroMoney()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {/* The ledger. */}
      <div className="bud-led">
        <div className="bud-led-head">
          <span className="bud-led-h">Invoices &amp; commitments</span>
          <span className="bud-led-count">{lines.length} {lines.length === 1 ? 'line' : 'lines'}</span>
          {hasLines && <span className="bud-led-hint">Select a line to open it</span>}
          {mayPlan && !adding && <button {...inert('bud-add-open', () => setAdding(true))}>+ Add a payment</button>}
        </div>

        {hasLines ? (
          <>
            <div className="lst">
              <div className="lst-head">
                <span>Date</span><span>Vendor / description</span><span className="r">Amount</span>
                <span>Pool</span><span>Phase</span><span>Billed against</span><span>Status</span><span aria-hidden="true" />
              </div>
              {lines.map((l) => {
                const word = INVOICE_STATUS_WORD[l.status];
                return (
                  <div
                    key={l.id}
                    className={`lr ${openLineId === l.id ? 'is-open' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenLine?.(l.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenLine?.(l.id); } }}
                    aria-label={`Open the ${l.vendor || 'budget'} line`}
                  >
                    <span className="lr-date tnum">{fmtDay(l.date)}</span>
                    <span className="lr-vd"><b>{l.vendor || '—'}</b>{l.description ? <span className="lr-desc"> {l.description}</span> : null}</span>
                    <span className="lr-amt tnum">
                      {isForeign(l.currency) ? (
                        <span className="bud-amt-cur">
                          <span className="face">{fmtIn(l.currency, l.amount)} <span className="bud-cur-chip">{l.currency}</span></span>
                          <span className="base tnum">≈ {fmtMoney(lineBaseAmount(l, base, fxRates))}</span>
                        </span>
                      ) : (
                        fmtMoney(l.amount)
                      )}
                    </span>
                    <span className="lr-pool"><span className={`bud-tag ${l.pool}`}>{l.pool === 'capex' ? 'Capex' : 'Opex'}</span></span>
                    <span className="lr-phase">{l.phase ? <span className="phase-chip"><span className="phase-chip-dot" />{phaseName(l.phase) ?? 'Other'}</span> : <span className="bud-ph-unassigned">—</span>}</span>
                    <span className="lr-billed">{l.milestone ? (() => { const n = milestoneName(l.milestone) ?? 'a milestone'; return <span className="phase-chip" title={n}><span className="phase-chip-dot" /><span className="lr-billed-name">{n}</span></span>; })() : <span className="bud-ph-unassigned">—</span>}</span>
                    <span className="lr-status"><span className={`st st-${INVOICE_STATUS_TONE[l.status]}`}><span className="st-dot" />{word}</span></span>
                    <span className="lr-trail">
                      {pendingCorrection(l) && <span className="lr-pending" title="A reclassification is awaiting approval"><Icon name="shield" size={12} /></span>}
                      {excIds.has(l.id) && <span className="lr-exc" title="Control exception — open the line to see it"><Icon name="warning" size={12} /></span>}
                      {mayPlan && <RowMenu onCorrect={() => setCorrectingLine(l)} />}
                      <span className="lr-chev"><Icon name="chevron" size={18} /></span>
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="gl-foot">
              <span>
                Committed <b className="tnum">{fmtMoney(rollup.committedTotal) || zeroMoney()}</b>
                {' · '}Paid (actual) <b className="tnum">{fmtMoney(rollup.actualTotal) || zeroMoney()}</b>
              </span>
              {rollup.foreignCount != null && <span className="gl-foot-note">in {currencyByCode(base).code} (base)</span>}
            </div>
          </>
        ) : (
          !adding && <p className="lst-empty">No invoices or commitments yet.{mayPlan ? ' Add a payment as each PO is raised or invoice comes in.' : ''}</p>
        )}

        {mayPlan && adding && (
          // form-opens: the payment editor glides in with the same drawn, seen rise
          // as the Edit-budget form, never a pop — R32 (Vinnie, 1 Nov).
          <div className="bud-add form-opens">
            <div className="bud-add-t">Add a payment</div>
            {problem && <p className="bud-add-problem">{problem}</p>}
            <div className="bud-add-grid">
              <label className="bud-fld bud-fld-amt">
                <span>Amount<span className="req">*</span></span>
                <input className="input tnum" inputMode="numeric" aria-required placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
              </label>
              <label className="bud-fld">
                <span>Currency<span className="req">*</span></span>
                <select className="input" aria-required value={curCode} onChange={(e) => pickCurrency(e.target.value, setCurCode, setRate)}>
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.symbol} — {c.name} ({c.code}){c.code === base ? ' · base' : ''}</option>
                  ))}
                </select>
              </label>
              {isForeign(curCode) && (
                <label className="bud-fld">
                  <span>Rate — 1 {curCode} in {base}<span className="req">*</span></span>
                  <input className="input tnum" inputMode="decimal" aria-required placeholder="e.g. 6.78" value={rate} onChange={(e) => setRate(e.target.value)} />
                  <span className="cur-rate-eq">{liveEquiv(amount, curCode, rate)}</span>
                </label>
              )}
              <label className="bud-fld bud-fld-vendor">
                <span>Vendor<span className="req">*</span></span>
                <input className="input" aria-required placeholder="Who it's with" value={vendor} onChange={(e) => setVendor(e.target.value)} />
              </label>
              <div className="bud-fld">
                <span>Pool<span className="req">*</span></span>
                <div className="bud-seg">
                  <button aria-pressed={pool === 'capex'} className={pool === 'capex' ? 'on' : ''} onClick={() => setPool('capex')}>Capex</button>
                  <button aria-pressed={pool === 'opex'} className={pool === 'opex' ? 'on' : ''} onClick={() => setPool('opex')}>Opex</button>
                </div>
              </div>
              <label className="bud-fld">
                <span>Phase</span>
                <select className="input" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
                  <option value="">Unassigned</option>
                  {livePh.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              {billableMs.length > 0 && (
                <label className="bud-fld">
                  <span>Billed against</span>
                  <select className="input" aria-label="Billed against a milestone" value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)}>
                    <option value="">Not against a milestone</option>
                    {billableMs.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}{m.billValue != null ? ` — ${fmtMoney(m.billValue)}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="bud-fld">
                <span>Status<span className="req">*</span></span>
                <div className="bud-seg">
                  <button aria-pressed={status === 'committed'} className={status === 'committed' ? 'on' : ''} onClick={() => setStatus('committed')}>Committed</button>
                  <button aria-pressed={status === 'paid'} className={status === 'paid' ? 'on' : ''} onClick={() => setStatus('paid')}>Paid</button>
                </div>
              </div>
              <label className="bud-fld">
                <span>{status === 'paid' ? 'Paid on' : 'Committed on'}</span>
                <DateField ariaLabel={status === 'paid' ? 'Paid on' : 'Committed on'} value={date} onChange={setDate} onInvalidChange={badDates.mark('adddate')} variant="form" />
              </label>
              <label className="bud-fld">
                <span>Payment due</span>
                <DateField ariaLabel="Payment due" value={dueDate} onChange={setDueDate} onInvalidChange={badDates.mark('adddue')} variant="form" />
              </label>
              <label className="bud-fld bud-fld-desc">
                <span>Description</span>
                <input className="input" placeholder="What it's for" value={description} onChange={(e) => setDescription(e.target.value)} />
              </label>
              <label className="bud-fld">
                <span>Invoice ref</span>
                <input className="input" placeholder="e.g. INV-2291" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} />
              </label>
              <label className="bud-fld">
                <span>PO number</span>
                <input className="input" placeholder="e.g. PO-1043" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
              </label>
              <div className="bud-fld bud-fld-desc">
                <span>Supplier invoice</span>
                {invoiceFile ? (
                  <div className="doc-chip">
                    <span className="doc-chip-ic"><Icon name="document" size={18} /></span>
                    <span className="doc-chip-name">{invoiceFile.name}</span>
                    <span className="doc-chip-meta">{Math.max(1, Math.round(invoiceFile.size / 1024))} KB</span>
                    <button className="doc-chip-act" type="button" onClick={() => setInvoiceFile(null)}>Remove</button>
                  </div>
                ) : (
                  <button className="doc-attach-btn" type="button" onClick={() => addFileRef.current?.click()}>
                    <Icon name="document" size={15} /> Attach the supplier invoice (PDF)
                  </button>
                )}
                <input ref={addFileRef} type="file" accept="application/pdf" hidden onChange={onPickAddFile} />
              </div>
            </div>
            <div className="bud-add-foot">
              <span className="bud-conv">Amounts ex-tax. A committed line becomes paid when the invoice is settled. A line in another currency converts to {currencyByCode(base).code} at the rate above; a paid line keeps the rate it was paid at.</span>
              <div className="bud-add-btns">
                <button className="btn btn-small" onClick={() => { resetAdd(); setAdding(false); }}>Cancel</button>
                <button className="btn btn-primary btn-small" onClick={submit} disabled={badDates.any || saving}>{saving ? 'Adding…' : 'Add payment'}</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {correctingLine && (
        <CorrectionDialog
          projectId={project.id}
          line={correctingLine}
          onClose={() => setCorrectingLine(null)}
        />
      )}

      {adjOpen && project.contractValue != null && (
        <ScopeAdjustDialog
          projectName={project.name}
          from={project.contractValue}
          baseline={project.budget}
          onClose={() => setAdjOpen(false)}
          onDone={(newValue, newBaseline) => {
            // Reflect the approved figures in the open form; stop the baseline
            // auto-sync fighting the value the dialog just set.
            setBContract(newValue);
            setBAmt(newBaseline);
            setBaselineTouched(false);
          }}
        />
      )}
    </div>
  );
}
